import React, { ReactNode, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link, useParams } from "react-router-dom";
import { AccountPageWrapper } from "../AccountPageWrapper";
import { Card, HStack, SimpleGrid, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { appRoutes } from "../../../../App";
import { AccountView } from "../../models/AccountView";
import { LoadingScreen } from "../../../shared/views/LoadingScreen/LoadingScreen";
import { useStores } from "../../../shared/stores/RootStore";

const AccountHomePageBase: React.FC = () => {
  const accountId = useParams().accountId!;
  const { accountStore } = useStores();
  const [initialLoadingDone, setInitialLoadingDone] = useState(false);
  const account = accountStore.account;
  const stats = accountStore.accountStats;

  useEffect(() => {
    document.title = `Account - Lane Patrol`;
  }, []);

  useEffect(() => {
    const initialLoad = async () => {
      setInitialLoadingDone(false);
      accountStore.resetAccountManagement();
      await accountStore.fetchAccountStats(accountId);
      setInitialLoadingDone(true);
    };
    initialLoad();
    // eslint-disable-next-line
  }, [accountId]);

  return (
    <AccountPageWrapper selectedView={AccountView.Home} accountId={accountId}>
      {initialLoadingDone ? (
        <>
          <Text mx={6} fontSize="2xl">
            {t(`account:Home.tab`)}
          </Text>

          <SimpleGrid mx={6} columns={{ md: 2, lg: 3 }} gap={4}>
            <MaybeLink
              disabled={!account?.permissions.canManageProjects}
              to={appRoutes.accountProjects(accountId)}
            >
              <HStack justifyContent="center">
                <Text fontSize="8xl" fontWeight="semibold" color="primary.600">
                  {stats?.nProjects}
                </Text>
                <Text alignSelf="end" lineHeight="90px" fontSize="lg">
                  {t(`account:Home.projects`)}
                </Text>
              </HStack>
            </MaybeLink>

            <MaybeLink
              disabled={!account?.permissions.canManageUsers}
              to={appRoutes.accountUsers(accountId)}
            >
              <HStack justifyContent="center">
                <Text fontSize="8xl" fontWeight="semibold" color="primary.600">
                  {stats?.nAccountUsers}
                </Text>
                <Text alignSelf="end" lineHeight="90px" fontSize="lg">
                  {t(`account:Home.users`)}
                </Text>
              </HStack>
            </MaybeLink>
          </SimpleGrid>
        </>
      ) : (
        <LoadingScreen />
      )}
    </AccountPageWrapper>
  );
};

export const AccountHomePage = observer(AccountHomePageBase);

interface MaybeLinkProps {
  disabled: boolean;
  to: string;
  children: ReactNode;
}
const MaybeLink: React.FC<MaybeLinkProps> = ({ disabled, to, children }) => {
  const container = (
    <Card.Root
      variant="outline"
      p={4}
      _hover={disabled ? undefined : { bgColor: "gray.50" }}
    >
      {children}
    </Card.Root>
  );
  if (disabled) return container;
  return <Link to={to}>{container}</Link>;
};
